import type { UsageByKeyEntry, UsageRequestLogEntry } from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import { useCatalog } from '../../api/useCatalog.ts';
import { useDisplayRate } from '../../api/useBilling.ts';
import {
  useUsageByKey,
  useUsageDaily,
  useUsageRequests,
  useUsageSummary,
} from '../../api/useUsage.ts';
import { StackedBars } from '../../charts/index.ts';
import { PanelError } from '../../components/PanelError.tsx';
import {
  formatTokens,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  unitAwareTokenCell,
} from '../../components/formatters.ts';
import { useLocale, useT } from '../../i18n/index.ts';
import {
  Chip,
  DateRangePicker,
  KpiCard,
  PageHeader,
  Panel,
  Skeleton,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { DateRangePreset } from '../../ui/index.ts';
import {
  dayRangeToIsoWindow,
  formatCompactCount,
  formatNullablePercent,
  formatRatio,
  nullableRatio,
  rollingDayRange,
  testIdSlug,
  todayLocalDay,
} from './format.ts';
import type { DayRange } from './format.ts';

/**
 * Member usage view (no `org:usage:read`) — epic #897 PR 6. Strictly
 * self-scoped: summary KPIs, a daily in/out token trend, per-key
 * aggregates, and the recent billed-request metadata list. The admin
 * dashboard endpoint is never called from this branch.
 */

const DEFAULT_RANGE = '7d';
// Same UI-side cap as the admin view so the two /usage screens agree.
const MAX_RANGE_SPAN_DAYS = 90;

function memberRangePresets(today: string, t: ReturnType<typeof useT>): readonly DateRangePreset[] {
  return [
    { id: 'today', label: t('usage.range.today'), range: rollingDayRange(today, 1) },
    { id: '7d', label: t('usage.range.7d'), range: rollingDayRange(today, 7) },
    { id: '30d', label: t('usage.range.30d'), range: rollingDayRange(today, 30) },
    { id: '90d', label: t('usage.range.90d'), range: rollingDayRange(today, 90) },
  ];
}

type MemberCacheSummary = {
  readonly successRequests: number;
  readonly requestCacheHitRate: number | null;
  readonly tokenCacheHitRate: number | null;
};

function summarizeMemberCache(keys: readonly UsageByKeyEntry[]): MemberCacheSummary {
  const totals = keys.reduce(
    (acc, key) => ({
      successRequests: acc.successRequests + key.successRequests,
      cacheHitRequests: acc.cacheHitRequests + key.cacheHitRequests,
      cacheEligibleInputTokens: acc.cacheEligibleInputTokens + key.cacheEligibleInputTokens,
      cacheReadTokens: acc.cacheReadTokens + key.cacheReadTokens,
    }),
    { successRequests: 0, cacheHitRequests: 0, cacheEligibleInputTokens: 0, cacheReadTokens: 0 },
  );
  return {
    successRequests: totals.successRequests,
    requestCacheHitRate: nullableRatio(totals.cacheHitRequests, totals.successRequests),
    tokenCacheHitRate: nullableRatio(totals.cacheReadTokens, totals.cacheEligibleInputTokens),
  };
}

/**
 * Display name for a by-key row's SKU (epic #898 P6 #913). Rows are one
 * per key×SKU; the stored `skuName` is the CANONICAL `namespace/model`,
 * so it is brand-aliased at render time through the catalog (`sku_id` →
 * brand-aliased `sku_name`). Legacy rows (null `skuId`) fall back to
 * their historical channel name.
 */
export function byKeyRowModelLabel(
  entry: UsageByKeyEntry,
  brandedSkuNameById: ReadonlyMap<string, string>,
): string {
  if (typeof entry.skuId === 'string' && entry.skuId.length > 0) {
    const branded = brandedSkuNameById.get(entry.skuId);
    if (branded !== undefined) return branded;
  }
  if (typeof entry.skuName === 'string' && entry.skuName.length > 0) return entry.skuName;
  if (typeof entry.channelName === 'string' && entry.channelName.length > 0) {
    return entry.channelName;
  }
  return '—';
}

/** Stable per-row identity now that a key can appear once per SKU. */
export function byKeyRowId(entry: UsageByKeyEntry): string {
  return `${entry.apiKeyId}:${entry.skuId ?? entry.channelId ?? 'legacy'}`;
}

export function MemberUsage(): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  // Preset selections stay id-only so their ranges track "today" across
  // renders; a concrete range is only pinned for custom picks.
  const [rangeState, setRangeState] = useState<{
    readonly presetId: string | null;
    readonly custom: DayRange | null;
  }>({ presetId: DEFAULT_RANGE, custom: null });
  const today = todayLocalDay();
  const rangePresets = useMemo(() => memberRangePresets(today, t), [today, t]);
  const activePreset =
    rangeState.presetId !== null
      ? rangePresets.find((preset) => preset.id === rangeState.presetId)
      : undefined;
  const dayRange: DayRange = activePreset?.range ?? rangeState.custom ?? rollingDayRange(today, 7);
  const queryRange = useMemo(() => dayRangeToIsoWindow(dayRange), [dayRange]);

  const summaryQ = useUsageSummary(queryRange);
  const dailyQ = useUsageDaily(queryRange);
  const byKeyQ = useUsageByKey(queryRange);
  const requestsQ = useUsageRequests({ ...queryRange, limit: 50, offset: 0 });
  const displayRateQ = useDisplayRate();
  const catalogQ = useCatalog();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const summary = summaryQ.data;
  const cacheSummary = useMemo(
    () => summarizeMemberCache(byKeyQ.data?.keys ?? []),
    [byKeyQ.data?.keys],
  );
  const brandedSkuNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const sku of catalogQ.data?.skus ?? []) map.set(sku.sku_id, sku.sku_name);
    return map;
  }, [catalogQ.data?.skus]);

  const rangeLabel = activePreset?.label ?? `${dayRange.from} → ${dayRange.to}`;

  return (
    <div data-testid="usage-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('usage.member.title')}
        sub={t('usage.member.subtitle')}
        testId="usage-member-header"
        actions={
          <DateRangePicker
            value={dayRange}
            onChange={(range, presetId) => {
              setRangeState(
                presetId !== null ? { presetId, custom: null } : { presetId: null, custom: range },
              );
            }}
            presets={rangePresets}
            activePresetId={rangeState.presetId}
            maxSpanDays={MAX_RANGE_SPAN_DAYS}
            locale={locale}
            ariaLabel={t('usage.range.aria_label')}
            labels={{
              prevMonth: t('usage.range.prev_month'),
              nextMonth: t('usage.range.next_month'),
            }}
            testId="usage-range-picker"
          />
        }
      />

      {summaryQ.isLoading || byKeyQ.isLoading ? (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6"
          data-testid="usage-member-loading"
        >
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <Skeleton key={item} className="h-28" />
          ))}
        </div>
      ) : summaryQ.isError ? (
        <Panel caption={t('usage.member.title')} padded testId="usage-member-error">
          <PanelError
            message={t('usage.error.load')}
            retryLabel={t('usage.retry')}
            onRetry={() => {
              void summaryQ.refetch();
            }}
          />
        </Panel>
      ) : summary !== undefined ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <KpiCard
            testId="usage-summary-input-tokens"
            label={t('usage.summary.tokens_in')}
            value={formatCompactCount(summary.totalInputTokens)}
            delta={{
              text: t('usage.member.summary.scope_detail', { range: rangeLabel }),
              tone: 'neutral',
            }}
          />
          <KpiCard
            testId="usage-summary-output-tokens"
            label={t('usage.summary.tokens_out')}
            value={formatCompactCount(summary.totalOutputTokens)}
            delta={{
              text: t('usage.requests_count', { count: formatTokens(summary.totalRequests) }),
              tone: 'neutral',
            }}
          />
          <KpiCard
            testId="usage-summary-cache-write-tokens"
            label={t('usage.summary.cache_write')}
            value={formatCompactCount(summary.totalCacheWriteTokens)}
            delta={{
              text: t('usage.member.summary.cache_write_detail', { range: rangeLabel }),
              tone: 'neutral',
            }}
          />
          <KpiCard
            testId="usage-summary-cache-read-tokens"
            label={t('usage.summary.cache_read')}
            value={formatCompactCount(summary.totalCacheReadTokens)}
            delta={{
              text: t('usage.member.summary.cache_read_detail', { range: rangeLabel }),
              tone: 'neutral',
            }}
          />
          <KpiCard
            testId="usage-summary-cost"
            label={t('usage.summary.spend')}
            value={formatUsdMicrosOrCentsAsCnyDisplay(
              summary.totalCostUsdMicros,
              summary.totalCostUsdCents,
              cnyPerUsd,
            )}
            delta={{ text: t('usage.member.summary.spend_detail'), tone: 'neutral' }}
          />
          <KpiCard
            testId="usage-summary-cache-hit"
            label={t('usage.summary.cache_hit')}
            value={formatNullablePercent(cacheSummary.requestCacheHitRate)}
            delta={{
              text: t('usage.member.summary.cache_hit_detail', {
                rate: formatNullablePercent(cacheSummary.tokenCacheHitRate),
                count: formatTokens(cacheSummary.successRequests),
              }),
              tone: 'neutral',
            }}
          />
        </div>
      ) : null}

      <Panel caption={t('usage.member.trend.title')} padded testId="usage-member-trend">
        {dailyQ.isLoading ? (
          <Skeleton className="h-48" />
        ) : dailyQ.isError ? (
          <PanelError
            message={t('usage.error.load')}
            retryLabel={t('usage.retry')}
            onRetry={() => {
              void dailyQ.refetch();
            }}
            testId="usage-member-daily-error"
          />
        ) : (dailyQ.data?.days ?? []).length === 0 ? (
          <p
            className="m-0 py-8 text-center text-dense text-paper-low"
            data-testid="usage-daily-empty"
          >
            {t('usage.admin.empty.window')}
          </p>
        ) : (
          <StackedBars
            buckets={(dailyQ.data?.days ?? []).map((day) => day.date)}
            series={[
              {
                id: 'input',
                label: t('usage.legend.input'),
                values: (dailyQ.data?.days ?? []).map((day) => day.inputTokens),
              },
              {
                id: 'output',
                label: t('usage.legend.output'),
                values: (dailyQ.data?.days ?? []).map((day) => day.outputTokens),
              },
            ]}
            height={200}
            ariaLabel={t('usage.member.trend.title')}
            testId="usage-daily-chart"
          />
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MemberKeyPanel
          keys={byKeyQ.data?.keys ?? []}
          isLoading={byKeyQ.isLoading}
          isError={byKeyQ.isError}
          onRetry={() => {
            void byKeyQ.refetch();
          }}
          cnyPerUsd={cnyPerUsd}
          brandedSkuNameById={brandedSkuNameById}
        />
        <MemberRequestPanel
          rows={requestsQ.data?.rows ?? []}
          isLoading={requestsQ.isLoading}
          isError={requestsQ.isError}
          onRetry={() => {
            void requestsQ.refetch();
          }}
        />
      </div>
    </div>
  );
}

function MemberKeyPanel(props: {
  readonly keys: readonly UsageByKeyEntry[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly cnyPerUsd: number | null;
  readonly brandedSkuNameById: ReadonlyMap<string, string>;
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('usage.member.keys.title')} testId="usage-member-keys">
      {props.isLoading ? (
        <div className="p-4">
          <TableSkeleton rows={3} />
        </div>
      ) : props.isError ? (
        <div className="p-4">
          <PanelError
            message={t('usage.error.load')}
            retryLabel={t('usage.retry')}
            onRetry={props.onRetry}
          />
        </div>
      ) : props.keys.length === 0 ? (
        <p className="m-0 p-4 text-dense text-paper-low">{t('usage.member.keys.empty')}</p>
      ) : (
        <Table className="rounded-none border-0" testId="usage-member-keys-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('usage.by_key.column.key_name')}</Th>
              <Th>{t('usage.by_key.column.model')}</Th>
              <Th num>{t('usage.by_key.column.requests')}</Th>
              <Th num>{t('usage.summary.tokens_in')}</Th>
              <Th num>{t('usage.summary.tokens_out')}</Th>
              <Th num>{t('usage.summary.cache_read')}</Th>
              <Th num>{t('usage.summary.cache_write')}</Th>
              <Th num>{t('usage.by_key.column.cost')}</Th>
            </Tr>
          </THead>
          <TBody>
            {props.keys.slice(0, 8).map((key) => (
              <Tr
                key={byKeyRowId(key)}
                data-testid={`usage-member-key-${testIdSlug(byKeyRowId(key))}`}
              >
                <Td heading>
                  <span className="block truncate font-medium text-paper">
                    {key.apiKeyName ?? key.apiKeyPrefix ?? t('usage.by_key.unnamed_key')}
                  </span>
                  <span className="block font-mono text-micro text-paper-low">
                    {t('usage.member.keys.cache_rates', {
                      request: formatNullablePercent(key.requestCacheHitRate),
                      token: formatNullablePercent(key.tokenCacheHitRate),
                      ratio: formatRatio(key.cacheReadWriteRatio),
                    })}
                  </span>
                </Td>
                <Td
                  mono
                  label={t('usage.by_key.column.model')}
                  data-testid={`usage-member-key-model-${testIdSlug(byKeyRowId(key))}`}
                >
                  {byKeyRowModelLabel(key, props.brandedSkuNameById)}
                </Td>
                <Td num label={t('usage.by_key.column.requests')}>
                  {formatTokens(key.requests)}
                </Td>
                <Td num label={t('usage.summary.tokens_in')}>
                  {formatCompactCount(key.inputTokens)}
                </Td>
                <Td num label={t('usage.summary.tokens_out')}>
                  {formatCompactCount(key.outputTokens)}
                </Td>
                <Td num label={t('usage.summary.cache_read')}>
                  {formatCompactCount(key.cacheReadTokens)}
                </Td>
                <Td num label={t('usage.summary.cache_write')}>
                  {formatCompactCount(key.cacheWriteTokens)}
                </Td>
                <Td num emphasis label={t('usage.by_key.column.cost')}>
                  {formatUsdMicrosOrCentsAsCnyMetered(
                    key.costUsdMicros,
                    key.costUsdCents,
                    props.cnyPerUsd,
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

function MemberRequestPanel(props: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('usage.member.requests.title')} testId="usage-member-requests">
      {props.isLoading ? (
        <div className="p-4">
          <TableSkeleton rows={3} />
        </div>
      ) : props.isError ? (
        <div className="p-4">
          <PanelError
            message={t('usage.error.load')}
            retryLabel={t('usage.retry')}
            onRetry={props.onRetry}
          />
        </div>
      ) : props.rows.length === 0 ? (
        <p className="m-0 p-4 text-dense text-paper-low">{t('usage.member.requests.empty')}</p>
      ) : (
        <Table className="rounded-none border-0" testId="usage-member-requests-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('requests.column.model')}</Th>
              <Th>{t('requests.column.request')}</Th>
              <Th num>{t('usage.metric.in')}</Th>
              <Th num>{t('usage.metric.out')}</Th>
              <Th>{t('requests.column.status')}</Th>
            </Tr>
          </THead>
          <TBody>
            {props.rows.slice(0, 8).map((row) => (
              <Tr key={row.id} data-testid={`usage-request-row-${testIdSlug(row.requestId)}`}>
                <Td mono heading>
                  {row.model}
                </Td>
                <Td mono label={t('requests.column.request')} className="text-paper-low">
                  {row.requestId}
                </Td>
                {/* #1670: unit-metered rows dash instead of showing a fake 0. */}
                <Td num label={t('usage.metric.in')}>
                  {unitAwareTokenCell(row, formatCompactCount(row.inputTokens))}
                </Td>
                <Td num label={t('usage.metric.out')}>
                  {unitAwareTokenCell(row, formatCompactCount(row.outputTokens))}
                </Td>
                <Td label={t('requests.column.status')}>
                  <Chip tone={row.statusCode >= 400 ? 'bad' : 'ok'}>{String(row.statusCode)}</Chip>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}
